import * as React from 'react';
import { OverlayTrigger, Tooltip, Popover, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCode } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';
import { Bloodwork } from '../../models/Bloodwork';
import { BloodworkRange } from '../../models/BloodworkRange';
import { FurtherConsiderations } from '../../models/FurtherConsiderations';
import { buildAIExportData } from '../../helpers/AIExportBuilder';

// The raw-data export, shared by the two places it is offered:
//   - the comparison screen, for EVERY test the client has ("Export All Client
//     Data"), and
//   - one analysis report's Insights card, for that report alone.
// One module because the two differ only in which tests they pass and what the
// file is called. The privacy paragraph below in particular is wording that was
// settled once and must not drift into two versions — see the comment on it.

// Short prompt shown in the export info popover for practitioners to copy and
// use with the downloaded file in an AI assistant. The file itself embeds the
// full prompt (AI_REVIEW_PROMPT, in its `instructions` field); this is the
// shorter "I already have the file open" version, so the two describe the SAME
// two-part output. Keep them in step: if the output shape changes in
// aiReviewPrompt.ts, it changes here too.
export const AI_FILE_PROMPT =
  'You are a functional-medicine analyst. Using ONLY the attached LabSmarts blood ' +
  'work export, write TWO parts. Part A, for the practitioner: a one-line ' +
  'snapshot, then findings numbered one per body system (grouping related ' +
  'markers into a single finding rather than re-listing values, which the ' +
  'practitioner already has), what changed across tests, and brief follow-ups. ' +
  'Part B, for the client: the same picture in plain, non-diagnostic language ' +
  'they can read, explaining what each finding means and what to do next. ' +
  'Open Part B with a short version: a sentence or two plus up to five ' +
  'one-line bullets covering what is improving, what needs attention, and ' +
  'what to do first, so a client who reads no further still has the ' +
  'picture. Keep Part B under 1,000 words. ' +
  'Both parts cover every marker the practitioner added themselves, ' +
  'including the ones filed under their own section names, which nothing ' +
  'else in the file flags, with movement across tests noted even where a ' +
  'value stays inside its range. ' +
  'Decision-support, not a diagnosis. Keep both parts tight: no blank line ' +
  'after a bold label and none between paragraphs, since the field they ' +
  'are pasted into adds no spacing of its own. Put each part in its own ' +
  'fenced code ' +
  'block so they can be copied one at a time: a label line saying which tab ' +
  'that part goes in, then the block holding that part alone, Part A first. ' +
  'No language tag on the fences, no backtick inside a block, and nothing ' +
  'between the two blocks.';

/** Button that copies the given text to the clipboard with brief "Copied!" feedback. */
export const CopyPromptButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const onCopy = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => undefined);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline-secondary"
      onClick={onCopy}
      style={{ marginTop: '8px' }}
    >
      {copied ? 'Copied!' : 'Copy prompt'}
    </Button>
  );
};

/**
 * Click popover with a prompt users can copy to use with the exported file.
 *
 * Two things differ between the callers. `scopeLine` says WHAT the file holds,
 * because the practitioner is choosing where to send it and has to know
 * whether it is one report or the client's whole history. `tabsLocation` names
 * the card the two tabs live in, which is NOT the same place on the two
 * screens ("the Insights panel" on an analysis report, "Overall Progress &
 * Trends" on the comparison report) — telling someone to paste into a tab
 * without saying which card holds it is half an instruction.
 */
export function rawDataExportPopover(
  id: string,
  scopeLine: string,
  tabsLocation: string,
) {
  return (
    <Popover id={id} className="export-prompt-popover">
      <Popover.Title
        as="div"
        style={{ fontWeight: 600, backgroundColor: '#fff', borderBottom: 'none' }}
      >
        Using this file with an AI
      </Popover.Title>
      <Popover.Content>
        <div style={{ textAlign: 'left' }}>
          {/* No assistants named, for the reason the transcribe screen's
              step 2 carries at length: naming one reads as clearing it. */}
          <div style={{ marginBottom: '6px' }}>{scopeLine}</div>
          <div style={{ marginBottom: '6px' }}>
            Download the file, then upload it to an AI assistant with this
            prompt:
          </div>
          <div style={{ fontStyle: 'italic' }}>&ldquo;{AI_FILE_PROMPT}&rdquo;</div>
          <CopyPromptButton text={AI_FILE_PROMPT} />
          <div style={{ marginTop: '8px', opacity: 0.85 }}>
            (The file also includes full instructions at the top.)
          </div>
          {/* The answer comes back in two parts for two audiences, and there
              are now two tabs to put them in. Without this the practitioner
              has to infer the mapping from the AI's own headings, and the
              cost of guessing wrong is the clinical half printing for the
              client. Both screens use the same two tab names, so this reads
              correctly from either one. */}
          <div
            style={{
              marginTop: '12px',
              paddingTop: '10px',
              borderTop: '1px solid #e3e3e3',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
              What comes back, and where it goes
            </div>
            <div style={{ marginBottom: '6px' }}>
              The answer comes back as two separate blocks, each with its own
              copy button, so each one pastes in a single step:
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong>Part A, &ldquo;For the Practitioner&rdquo;</strong> is the
              clinical read, with values and marker names. Paste it into the{' '}
              <strong>Practitioner Notes</strong> tab of {tabsLocation}, which is
              never printed in the PDF.
            </div>
            <div>
              <strong>Part B, &ldquo;Your Blood Work Summary&rdquo;</strong> is
              written in plain language for the client. Paste it into the{' '}
              <strong>Client Summary</strong> tab of {tabsLocation}, which is the
              half that prints in the PDF.
            </div>
          </div>
          {/* The practitioner is the one choosing where this file goes, so
              this line is a heads-up, not a gate. It says what the file does
              and does not contain, because "names are removed" on its own
              reads as "anonymous" and the notes are free text the
              practitioner typed. */}
          <div style={{ marginTop: '10px', fontSize: '12px', opacity: 0.85 }}>
            The file holds the client&apos;s health information. The name and
            date of birth are removed, and age, sex, and collection dates
            remain, so it is de-identified but not anonymous, and any notes
            are included as written. Use an{' '}
            <strong>
              account your practice is permitted to use for client health
              information
            </strong>
            , and follow your own privacy obligations.
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
}

type ControlProps = {
  /** Visible label, e.g. "Export All Client Data" or "Export Raw Data". */
  label: string;
  /** Hover text on the download icon. */
  tooltip: string;
  /** One line in the popover saying what this particular file holds. */
  scopeLine: string;
  /**
   * The card holding the Client Summary / Practitioner Notes tabs on THIS
   * screen, named as the practitioner sees it: "the Insights panel" on an
   * analysis report, "Overall Progress & Trends" on the comparison report.
   */
  tabsLocation: string;
  onExport: () => void;
  /** Distinguishes this control's overlay ids from the other caller's. */
  idSuffix: string;
  /**
   * True when the control sits in a header that is itself a click target (the
   * Insights card toggles collapsed on header click). Stops the click there so
   * opening the popover or downloading the file does not also fold the card.
   */
  stopPropagation?: boolean;
  /**
   * Overrides for the wrapper, for a container that spaces its children
   * differently.
   *
   * The default 8px is the gap EVERY other control group in a toolbar uses
   * between its label, its info icon and its input ("Hide Alarm", "Hide Lab",
   * "Hide Blank Rows"). The larger gap a toolbar sets on its own span (14px on
   * the comparison report, 12px on the analysis report) is the space BETWEEN
   * groups, which is what makes each group read as one thing. Matching that
   * outer number inside the control made this group look loose beside the
   * others.
   *
   * Font size is deliberately NOT set here by default: the
   * control inherits it, so it lands at whatever its neighbours use. That is
   * 14px inside a card header (the `.card-header` base, which is what
   * "Include in PDF" and the other header controls inherit) and 12px in the
   * comparison report's toolbar, whose own span sets that. Hard-coding 12px
   * here made it correct in the one toolbar it was written for and too small
   * everywhere else.
   */
  style?: React.CSSProperties;
  /**
   * Inline font-size for the two icons, for a toolbar that sets its own.
   *
   * Omit it on an uncrowded surface and index.css supplies the house sizes:
   * .fa-info-circle 16px, .fa-file-code 20px (alongside .fa-file-download and
   * .fa-file-pdf), a deliberate hierarchy where the icon you click to DO
   * something outweighs the one you click to LEARN something.
   *
   * The comparison report's toolbar is the exception and passes 15px. That row
   * carries seven controls, so its text runs at 12px and it scales EVERY icon
   * down to 15px inline, its own Export-to-PDF icons included. Inheriting the
   * house 20px there made this one file icon larger than the PDF icons beside
   * it. It has to be inline rather than a font-size on the wrapper, because
   * the .fa-* rules set font-size on the svg itself and so beat inheritance.
   */
  iconSize?: string;
};

/** Label + info popover + download icon, as it appears in a card header. */
export const RawDataExportControl: React.FC<ControlProps> = ({
  label,
  tooltip,
  scopeLine,
  tabsLocation,
  onExport,
  idSuffix,
  stopPropagation,
  style,
  iconSize,
}) => {
  const swallow = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
  };
  return (
    <span
      className="d-flex align-items-center"
      style={{ gap: '8px', ...style }}
      onClick={swallow}
    >
      <span>{label}</span>
      <OverlayTrigger
        trigger="click"
        rootClose
        overlay={rawDataExportPopover(
          `export-prompt-popover-${idSuffix}`,
          scopeLine,
          tabsLocation,
        )}
        placement="bottom"
      >
        {/* No inline font-size on either icon. index.css already sets the
            house sizes — .fa-info-circle at 16px, .fa-file-code at 20px,
            alongside .fa-file-download and .fa-file-pdf — and that gap is a
            deliberate hierarchy: the icon you click to DO something is heavier
            than the one you click to LEARN something. Hard-coding 15px here
            (copied from the comparison toolbar, which overrides every icon to
            15px) flattened it, and left this file icon visibly smaller than
            the Export-to-PDF icons sitting beside it in the analysis
            toolbar. */}
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{ color: 'white', cursor: 'pointer', fontSize: iconSize }}
        />
      </OverlayTrigger>
      <OverlayTrigger
        overlay={
          <Tooltip id={`export-json-tooltip-${idSuffix}`}>
            <div>{tooltip}</div>
          </Tooltip>
        }
        placement="bottom"
      >
        <FontAwesomeIcon
          icon={faFileCode}
          onClick={onExport}
          style={{ color: 'white', cursor: 'pointer', fontSize: iconSize }}
        />
      </OverlayTrigger>
    </span>
  );
};

type DownloadOptions = {
  /**
   * The tests to export, in any order — buildAIExportData sorts them and links
   * each to the one before it by collection date.
   */
  bloodworks: Bloodwork[];
  bloodworkRanges: BloodworkRange[];
  furtherConsiderations: FurtherConsiderations;
  /** From the LIVE client, never a per-test snapshot. See buildAIExportData. */
  overallPractitionerNotes?: string;
  comparisonSectionNotes?: { [sectionKey: string]: string };
  enabledSupplementBrands?: string[];
  /**
   * Filename stem, which is what tells the two downloads apart in a Downloads
   * folder: `blood_work_raw_data` is every test the client has,
   * `blood_work_report` is the one report that was open. Both carry the
   * client's initials so two clients' files can't be confused.
   */
  filenameStem: string;
  /**
   * The date to name the file with. Defaults to the latest collection date in
   * `bloodworks`, which for a one-report export is that report's own date.
   */
  filenameDate?: string;
};

/**
 * Build the raw-data export and hand it to the browser as a download.
 *
 * Both callers pass includeRecommendedSupplements and includeMvx: this is the
 * practitioner's own download, which is the one place those are allowed. The
 * client-facing "Overall Progress & Trends" draft calls buildAIExportData
 * directly and passes neither — see the options' docblocks for why.
 */
export function downloadRawDataExport(opts: DownloadOptions): void {
  const {
    bloodworks,
    bloodworkRanges,
    furtherConsiderations,
    overallPractitionerNotes,
    comparisonSectionNotes,
    enabledSupplementBrands,
    filenameStem,
    filenameDate,
  } = opts;

  const exportData = buildAIExportData(
    bloodworks,
    bloodworkRanges,
    furtherConsiderations,
    overallPractitionerNotes,
    {
      includeRecommendedSupplements: true,
      includeMvx: true,
      // Both download paths want the practitioner's own Insights note; the
      // client-facing draft, which calls buildAIExportData directly, must not
      // have it. See the option's docblock.
      includePractitionerInsights: true,
      enabledSupplementBrands,
      comparisonSectionNotes,
    },
  );

  const jsonString = JSON.stringify(exportData, null, 2);
  // The contents are JSON, but the file ships as .txt (text/plain): some AI
  // assistants reject a .json upload outright, and every one of them accepts
  // plain text. The practitioner should never have to rename the download.
  // Declare UTF-8 so consumers don't fall back to Latin-1/CP1252 and mojibake
  // the µ in units (µmol/L, etc.). Direction glyphs are already spelled out.
  const blob = new Blob([jsonString], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  // Name the file with the client's initials + a collection date, so the
  // practitioner can recognize the download. This is only for their local
  // use — the AI receives the JSON contents, not the filename.
  const client = bloodworks && bloodworks[0] ? bloodworks[0].client : undefined;
  const initials = client
    ? `${(client.firstName || '').charAt(0)}${(client.lastName || '').charAt(0)}`
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
    : '';
  const collectionDates = (bloodworks || [])
    .map(bw => bw && bw.collectionDate)
    .filter(Boolean) as string[];
  const latestCollectionDate = collectionDates.length
    ? collectionDates.reduce((a, b) => (a > b ? a : b))
    : undefined;
  const datePart = moment(filenameDate || latestCollectionDate).format('YYYY-MM-DD');
  const filename = initials
    ? `${filenameStem}_${initials}_${datePart}.txt`
    : `${filenameStem}_${datePart}.txt`;

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
